import useSWR, { mutate } from "swr";
import { useCallback, useState } from "react";
import { buyerAddressesService } from "../../api_client";
import type {
  BuyerAddressDto,
  CreateBuyerAddressDto,
  PartnersQueryDto,
} from "../../../api";

/**
 * Buyer address-book entry type. Mirrors the backend `BuyerAddressType` enum
 * (which in turn maps 1:1 to Odoo `res.partner.type`). Kept as a string union
 * so the FE doesn't depend on the backend's enum import.
 */
export type BuyerAddressType = "delivery" | "invoice" | "other";

const BASE_KEY = "/me/addresses";

function keyFor(type?: BuyerAddressType) {
  return type ? `${BASE_KEY}?type=${type}` : BASE_KEY;
}

/**
 * SWR hook over `GET /me/addresses`. Pass a `type` to narrow to delivery /
 * invoice / other entries; omit it to fetch the buyer's full address book.
 *
 * The type is part of the SWR cache key, so each filter is cached
 * independently and revalidated independently after a mutation.
 */
export function useMyAddresses(type?: BuyerAddressType) {
  return useSWR<BuyerAddressDto[]>(keyFor(type), async () => {
    const res = await buyerAddressesService.listMyAddresses(
      // The first argument is the query object. The generator typed it as
      // PartnersQueryDto (schema-name collision with the BE's
      // ListBuyerAddressesQuery) — TODO: drop the cast once the BE names that
      // DTO distinctly and the client is regenerated. Passing an axios config
      // here instead sent `?params.type=…`, which the BE ignores, so every
      // picker listed every address type.
      type ? ({ type } as unknown as PartnersQueryDto) : undefined,
    );
    return res.data;
  });
}

/**
 * Mutations for the buyer's address book. Revalidates *every* cached
 * `useMyAddresses(...)` after a write so all type-filtered views stay in
 * sync — a new `invoice` address shows up in both the unfiltered list and
 * the `invoice` filtered list without callers wiring per-type revalidation.
 */
export function useAddressMutations() {
  const [isCreating, setCreating] = useState(false);
  const [isArchiving, setArchiving] = useState(false);
  const [isSettingDefault, setSettingDefault] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const revalidateAll = async () => {
    await mutate(
      (key) => typeof key === "string" && key.startsWith(BASE_KEY),
      undefined,
      { revalidate: true },
    );
  };

  const create = useCallback(
    async (
      dto: CreateBuyerAddressDto & { type?: BuyerAddressType },
    ) => {
      setCreating(true);
      setError(null);
      try {
        // `type` is accepted by the BE today but the generated DTO doesn't
        // expose it until the next regen. Cast at the boundary; remove the
        // cast once `CreateBuyerAddressDto` includes `type`.
        const res = await buyerAddressesService.createMyAddress(
          dto as CreateBuyerAddressDto,
        );
        await revalidateAll();
        return res.data;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setCreating(false);
      }
    },
    [],
  );

  const archive = useCallback(async (id: number) => {
    setArchiving(true);
    setError(null);
    try {
      await buyerAddressesService.archiveMyAddress(id);
      await revalidateAll();
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setArchiving(false);
    }
  }, []);

  /** Makes a delivery address the default; the BE then lists it first. */
  const setDefault = useCallback(async (id: number) => {
    setSettingDefault(true);
    setError(null);
    try {
      await buyerAddressesService.setMyDefaultAddress(id);
      await revalidateAll();
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setSettingDefault(false);
    }
  }, []);

  return {
    create,
    archive,
    setDefault,
    isCreating,
    isArchiving,
    isSettingDefault,
    error,
  };
}
